<template>
	<div
		data-component="preorder-drawer"
		:class="{ '-open': isOpen }"
		:aria-hidden="(!isOpen).toString()"
	>
		<div class="backdrop" @click="onBackdropClick" />

		<section
			ref="panelRef"
			class="panel"
			data-lenis-prevent
			role="dialog"
			aria-modal="true"
			:aria-labelledby="dialogTitleId"
			:aria-describedby="dialogDescriptionId"
			tabindex="-1"
			@click.self="onBackdropClick"
		>
			<div class="panel-push" aria-hidden="true" />

			<div class="sheet-body">
				<header class="topbar">
					<div class="g-row">
						<div class="g-col xxl-6 sm-8 xs-8">
							<button
								type="button"
								class="close-button -p-medium hover-opacity"
								@click="requestClose"
							>
								{{ closeLabel }}
							</button>
						</div>
						<div class="g-col xxl-10 sm-8 xs-8">
							<p :id="dialogTitleId" class="top-label -p-medium">{{ title }}</p>
						</div>
						<div class="g-col xxl-8 sm-8 xs-8">
							<p class="book-label -p-medium">{{ bookLabel }}</p>
						</div>
					</div>
					<div class="topbar-progress" aria-hidden="true">
						<div class="topbar-progress-fill" :style="progressBarStyle" />
					</div>
				</header>

				<div class="content">
					<div class="g-row">
						<p
							:id="dialogDescriptionId"
							class="description g-indent -title2 g-col xxl-21 md-18 sm-24"
						>
							{{ description }}
						</p>
					</div>
					<Form
						:key="formRenderKey"
						class="preorder-form"
						:fields="preOrderFields"
						:label="submitLabel"
						@submitted="onFormSubmitted"
					/>
				</div>
			</div>
		</section>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useEmitter } from '~/composables/core'
import { Form } from '@/components/fragments/Form'

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	title: { type: String, default: 'Pre-Order' },
	bookLabel: { type: String, default: '↓ Bürobook' },
	description: { type: String, default: '' },
	closeLabel: { type: String, default: 'Close' },
	nameLabel: { type: String, default: 'Your Name' },
	namePlaceholder: { type: String, default: 'Your name' },
	emailLabel: { type: String, default: 'Your E-mail' },
	emailPlaceholder: { type: String, default: 'Your e-mail' },
	submitLabel: { type: String, default: 'Send Request' },
	privacyUrl: { type: String, default: 'https://burocratik.com/privacy-policy' },
	privacyLabel: { type: String, default: 'Privacy Policy' },
})

const emit = defineEmits(['update:modelValue', 'close'])

const emitter = useEmitter()
const panelRef = ref(null)
const previousFocusElement = ref(null)
const formRenderKey = ref(0)
const isOpen = computed(() => props.modelValue)
const scrollProgress = ref(0)
const progressBarStyle = computed(() => ({ transform: `scaleX(${scrollProgress.value})` }))
const preOrderFields = computed(() => [
	{
		name: 'name',
		label: props.nameLabel,
		type: 'string',
		placeholder: props.namePlaceholder,
	},
	{
		name: 'email',
		label: props.emailLabel,
		type: 'email',
		placeholder: props.emailPlaceholder,
	},
])
const dialogTitleId = 'preorder-drawer-title'
const dialogDescriptionId = 'preorder-drawer-description'
let hasLockedScroller = false

function onFormSubmitted() {
	formRenderKey.value += 1
	requestClose()
}

function updateScrollProgress() {
	const panel = panelRef.value

	if (!panel) {
		scrollProgress.value = 0
		return
	}

	const maxScroll = panel.scrollHeight - panel.clientHeight

	if (maxScroll <= 0) {
		scrollProgress.value = 0
		return
	}

	const ratio = panel.scrollTop / maxScroll
	scrollProgress.value = Math.min(1, Math.max(0, ratio))
}

function onPanelScroll() {
	updateScrollProgress()
}

function attachPanelScrollListener() {
	panelRef.value?.addEventListener('scroll', onPanelScroll, { passive: true })
}

function detachPanelScrollListener() {
	panelRef.value?.removeEventListener('scroll', onPanelScroll)
}

function focusFirstFormInput() {
	const panel = panelRef.value

	if (!panel) return

	const firstInput = panel.querySelector(
		'input[type="text"], input[type="email"], textarea, select',
	)

	if (firstInput && typeof firstInput.focus === 'function') {
		firstInput.focus()
		return
	}

	panel.focus()
}

function requestClose() {
	if (!isOpen.value) return

	emit('update:modelValue', false)
	emit('close')
}

function onBackdropClick() {
	requestClose()
}

function getFocusableElements() {
	if (!panelRef.value) return []

	return [
		...panelRef.value.querySelectorAll(
			'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		),
	].filter((element) => {
		if (element.hasAttribute('disabled')) return false
		if (element.getAttribute('aria-hidden') === 'true') return false
		return !element.hasAttribute('inert')
	})
}

function trapFocus(event) {
	const focusableElements = getFocusableElements()

	if (!focusableElements.length) {
		event.preventDefault()
		panelRef.value?.focus()
		return
	}

	const firstElement = focusableElements[0]
	const lastElement = focusableElements[focusableElements.length - 1]
	const activeElement = document.activeElement
	const focusInsidePanel = panelRef.value?.contains(activeElement)

	if (!focusInsidePanel) {
		event.preventDefault()
		firstElement.focus()
		return
	}

	if (event.shiftKey && activeElement === firstElement) {
		event.preventDefault()
		lastElement.focus()
		return
	}

	if (!event.shiftKey && activeElement === lastElement) {
		event.preventDefault()
		firstElement.focus()
	}
}

function onWindowKeydown(event) {
	if (!isOpen.value) return

	if (event.key === 'Escape') {
		event.preventDefault()
		requestClose()
		return
	}

	if (event.key === 'Tab') {
		trapFocus(event)
	}
}

function unlockAndRestoreFocus() {
	if (hasLockedScroller) {
		emitter.emit('scroller-unlock')
		hasLockedScroller = false
	}

	detachPanelScrollListener()
	window.removeEventListener('keydown', onWindowKeydown)

	if (previousFocusElement.value && typeof previousFocusElement.value.focus === 'function') {
		previousFocusElement.value.focus()
	}

	previousFocusElement.value = null
}

watch(
	isOpen,
	async (open) => {
		if (!import.meta.client) return

		if (open) {
			previousFocusElement.value =
				document.activeElement instanceof HTMLElement ? document.activeElement : null

			if (!hasLockedScroller) {
				emitter.emit('scroller-lock')
				hasLockedScroller = true
			}

			window.removeEventListener('keydown', onWindowKeydown)
			window.addEventListener('keydown', onWindowKeydown)

			await nextTick()
			const panel = panelRef.value

			if (panel) {
				panel.scrollTop = 0
			}

			scrollProgress.value = 0
			detachPanelScrollListener()
			attachPanelScrollListener()
			updateScrollProgress()

			focusFirstFormInput()
			return
		}

		unlockAndRestoreFocus()
	},
	{ immediate: true },
)

onBeforeUnmount(() => {
	if (!import.meta.client) return

	detachPanelScrollListener()
	window.removeEventListener('keydown', onWindowKeydown)

	if (hasLockedScroller) {
		emitter.emit('scroller-unlock')
		hasLockedScroller = false
	}
})
</script>

<style lang="scss" scoped>
[data-component='preorder-drawer'] {
	position: fixed;
	inset: 0;
	z-index: 2000;
	pointer-events: none;

	.backdrop {
		position: absolute;
		inset: 0;
		background-color: rgba(0, 0, 0, 0.2);
		opacity: 0;
		transition: opacity 0.35s ease;
	}

	.panel {
		position: absolute;
		inset: auto 0 0 0;
		height: 100svh;
		transform: translateY(100%);
		transition: transform 0.48s cubic-bezier(0.2, 0.82, 0.2, 1);
		will-change: transform;
		overflow-y: auto;
		overscroll-behavior: contain;
		-webkit-overflow-scrolling: touch;
	}

	.panel-push {
		height: 20svh;
		pointer-events: none;
	}

	.sheet-body {
		background-color: #ebebeb;
		min-height: 80svh;
		padding-bottom: rs(100, 100);
	}

	&.-open {
		pointer-events: all;

		.backdrop {
			opacity: 1;
		}

		.panel {
			transform: translateY(0%);
		}
	}

	.topbar {
		position: sticky;
		top: 0;
		z-index: 5;
		background-color: #ebebeb;
		padding-top: rs(22, 18);
		margin-bottom: rs(38, 30);
	}

	.topbar-progress {
		position: relative;
		width: 100%;
		height: 1px;
		margin-top: rs(20, 16);
		background-color: rgba(0, 0, 0, 0.5);
		overflow: hidden;
	}

	.topbar-progress-fill {
		position: absolute;
		inset: 0;
		background-color: rgba(0, 0, 0, 1);
		transform: scaleX(0);
		transform-origin: 0 50%;
		will-change: transform;
	}

	.top-label {
		white-space: nowrap;
	}

	.book-label {
		text-align: right;
		white-space: nowrap;
	}

	.close-button {
		width: max-content;
	}

	.content {
		padding-bottom: rs(24, 18);
	}

	.description {
		margin-bottom: rs(78, 46);
		// font-size: rs(74, 40);
		// line-height: 0.98;
		// letter-spacing: -0.06em;
	}

	.content :deep(._form) {
		margin-bottom: 0;
	}

	.hover-opacity {
		transition: opacity 0.2s ease;
	}

	@media (hover: hover) and (pointer: fine) {
		.hover-opacity:hover {
			opacity: 0.5;
		}
	}

	button:focus-visible,
	input:focus-visible,
	a:focus-visible,
	.panel:focus-visible {
		outline: 1px solid rgba(0, 0, 0, 0.7);
		outline-offset: 2px;
	}

	@include max-width($xs) {
		.topbar {
			margin-bottom: rs(26, 22);
		}

		.top-label,
		.book-label {
			text-align: left;
		}

		.description {
			font-size: rs(54, 32);
			line-height: 0.98;
			margin-bottom: rs(34, 30);
		}
	}
}
</style>
